import React, {useCallback, useEffect, useMemo, useState} from "react";
import {ComponentPropsFromRoute} from "../../../../routers/routers";
import {Button, Space, Tag} from "antd";
import {useTranslate} from "../../../../hooks/translate.hook";
import FilteredTable from "../../../common/filtered-table/filtered.table";
import {ColumnsType} from "antd/es/table";
import i18n from "i18next";
import {ColumnsConfig, convertTableColumnsToColumnConfigs} from "../../../../utils/table";
import {FilterDataHandler} from "../../../../models/filter/filter.data.handler";
import {columnsFactory} from "./statements-view.items.columns";
import {useParams} from "react-router-dom";
import {LoadingSpin} from "../../../common/loading-spin/loading.spin";
import {FieldGroupConfig} from "../../../../models/filter/filter.group";
import {filterGroupsFactory} from "./statements-view.items.filter-groups";
import {FilterButtonConfig} from "../../../../models/filtered-table/filtered.table.props";
import {FieldGroupSnapshot} from "../../../../models/filter/filter.field.snapshot";
import {Export} from "../../../common/export/export";
import {PageHeader} from "@ant-design/pro-components";
import {dailyStatementService} from "../../../../services/accounting/daily-statement.service";
import {
    DailyStatement,
    DailyStatementItem,
    DailyStatementItemStatus, DailyStatementItemType
} from "../../../../models/accounting/daily-statements";
import Date from "../../../date/date";

export enum FilterButtonsTypes {
    OKAY = "okay",
    NOT_OKAY = "not_okay",
    NOT_FOUND = "not_found",
    DIFF_MONTH = "diff_month",
    FREE_BALANCE = "free_balance"
}

export const AccountingDailyStatementsView: React.FC<ComponentPropsFromRoute> = ({name}) => {

    const { id } = useParams<{ id: string }>();

    const t = useTranslate();

    const [disabled, setDisabled] = useState<boolean>(false);

    const dataHandler: FilterDataHandler<DailyStatementItem> = useMemo(() => new FilterDataHandler(request => dailyStatementService.getAllItemsForStatement(parseInt(id!), request)), []);

    const columns: ColumnsType<DailyStatementItem> = useMemo(() => columnsFactory(t, disabled), [i18n.language, disabled]);
    const columnsConfigs: ColumnsConfig = useMemo(() => convertTableColumnsToColumnConfigs<DailyStatementItem>(columns), [i18n.language])
    const fcgConfigs: FieldGroupConfig[] = useMemo(() => filterGroupsFactory(t), []);

    const [statement, setStatement] = useState<DailyStatement | null>(null);

    const [exportModalShown, setExportModalShown] = useState<boolean>(false);

    const filterButtonConfigs: FilterButtonConfig<FilterButtonsTypes>[] = [
        {
            type: FilterButtonsTypes.OKAY,
            text: "OKAY",
            filters: [
                {
                    name: "statuses",
                    snapshots: [
                        {
                            rule: undefined,
                            name: "statuses",
                            value: DailyStatementItemStatus.OK
                        },
                    ],
                },
            ],
        },
        {
            type: FilterButtonsTypes.NOT_OKAY,
            text: "MISSMATCHES",
            filters: [
                {
                    name: "statuses",
                    snapshots: [
                        {
                            rule: undefined,
                            name: "statuses",
                            value: [
                                DailyStatementItemStatus.DIFFERENT_DATE,
                                DailyStatementItemStatus.DIFFERENT_GRAND_TOTAL,
                                DailyStatementItemStatus.DIFFERENT_SUBTOTAL,
                                DailyStatementItemStatus.DIFFERENT_TAX
                            ].join(","),
                        },
                    ],
                },
            ],
        },
        {
            type: FilterButtonsTypes.DIFF_MONTH,
            text: "ACCOUNTING.STATEMENTS.ITEM.STATUS.DIFFERENT_MONTH",
            filters: [
                {
                    name: "statuses",
                    snapshots: [
                        {
                            rule: undefined,
                            name: "statuses",
                            value: DailyStatementItemStatus.DIFFERENT_MONTH
                        },
                    ],
                },
            ],
        },
        {
            type: FilterButtonsTypes.NOT_FOUND,
            text: "ACCOUNTING.STATEMENTS.ITEM.STATUS.INVOICE_NOT_FOUND",
            filters: [
                {
                    name: "statuses",
                    snapshots: [
                        {
                            rule: undefined,
                            name: "statuses",
                            value: [
                                DailyStatementItemStatus.INVOICE_NOT_FOUND,
                                DailyStatementItemStatus.DOCUMENT_1C_NOT_FOUND,
                            ].join(",")
                        },
                    ],
                },
            ],
        },
        {
            type: FilterButtonsTypes.FREE_BALANCE,
            text: "Free Balance",
            filters: [
                {
                    name: "types",
                    snapshots: [
                        {
                            rule: undefined,
                            name: "types",
                            value: [
                                DailyStatementItemType.FREE_BALANCE,
                            ].join(",")
                        },
                    ],
                },
            ],
        }
    ];

    const defaultFilters: FieldGroupSnapshot[] = [
        {
            name: "statuses",
            snapshots: [
                {
                    rule: undefined,
                    name: "statuses",
                    value: [
                        DailyStatementItemStatus.DIFFERENT_DATE,
                        DailyStatementItemStatus.DIFFERENT_GRAND_TOTAL,
                        DailyStatementItemStatus.DIFFERENT_SUBTOTAL,
                        DailyStatementItemStatus.DIFFERENT_TAX
                    ].join(",")
                },
            ],
        }
    ]

    useEffect(() => {
        dailyStatementService.get(parseInt(id!)).then(statement => setStatement(statement))
    }, [id])

    if (!statement) {
        return <LoadingSpin />;
    }

    return <Space direction="vertical" size={[20, 20]}>

        <PageHeader
            style={{padding: 0}}
            title={"Statement"}
            className="site-page-header"
            subTitle={<>Date: <Date dateString={statement.onDate} /></>}
            tags={<Tag color="blue">{statement.mismatches} (mismatches)</Tag>}
        />

        <Button
            onClick={() => setExportModalShown(true)}
            type={"primary"}
        >
            {t("REPORTS.DOWNLOAD")}
        </Button>

        <FilteredTable
            columns={columns}
            columnsConfigs={{columns: columnsConfigs, pageKey: name}}
            dataHandler={dataHandler}
            filterGroups={fcgConfigs}
            filterButtons={filterButtonConfigs}
            filters={defaultFilters}
        />

        <Export
            title={"DOWNLOAD.DO_DOWNLOAD"}
            isShown={exportModalShown}
            withBasicExportFields
            config={{
                link: '/api/v3/accounting/monthly-statement/' + id + '/items',
                query: dataHandler.lastRequest?.toParams()
            }}
            onClose={() => setExportModalShown(false)}
        />
    </Space>;
}